import { Inject, Injectable, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CartItemAndDelivery } from 'app/_components/checkout-line-item-delivery/checkout-line-item-delivery.component.state';
import { CheckoutValidationMessages } from 'app/_components/checkout-validation/checkout-validation.messages';
import { CheckoutValidationService } from 'app/_components/checkout-validation/checkout-validation.service';
import {
  AdditionalDetailsFormGroup, CheckoutDetailsFormGroup, CheckoutFormGroup,
  CheckoutFormGroups,
  CheckoutSmsOptFormGroup, PaymentDetailsFormGroup,
  ShippingAndDeliveryFormGroup, ShippingAndDeliveryShipToFormGroup
} from 'app/_components/checkout/checkout.component';
import { REGEX_PHONE_PATTERN, UNIT_NUMBER_PATTERN } from 'app/app.constants';
import { billToType, i2pType, selectedPayment } from 'entities/checkout-billing-enums';
import { DeliveryItems } from 'entities/delivery-items';
import { OrderType } from 'entities/order-type';
import { take } from 'rxjs';
import { validateLineItem } from 'validators/delivery-line-by-line.validator';
import { i2pEmailValidator, isValidEmail } from 'validators/email.validator';
import { FeatureFlagService } from './feature-flag.service';
@Injectable({
  providedIn: "root"
})
export class CheckoutFormService {
  public checkoutForm: FormGroup<CheckoutFormGroup>;

  constructor(
    private featureFlagService: FeatureFlagService,
    private formBuilder: FormBuilder,
    private checkoutFormValidation: CheckoutValidationService,
    @Inject(REGEX_PHONE_PATTERN) public regexPhonePattern: RegExp
  ) {
    this.initForm();
  }

  private unitNumberPattern = inject(UNIT_NUMBER_PATTERN);


  public initForm(): void {
    const isCustomerNotesFeatureEnabled = this.featureFlagService.isFeatureActive("FeatureFlag.CustomerNotes.Enabled");
    const newForm = this.formBuilder.group<CheckoutFormGroup>({
      checkoutDetails: this.formBuilder.group<CheckoutDetailsFormGroup>({
        transactionType: this.formBuilder.control<OrderType | null>(null, Validators.required),
        orderChannel: this.formBuilder.control(null),
        poNumber: this.formBuilder.control(''),
        unitNumber: this.formBuilder.control('', {validators: Validators.pattern(this.unitNumberPattern)})
      }),
      shippingAndDelivery: this.formBuilder.group<ShippingAndDeliveryFormGroup>({
        deliveryItems: this.formBuilder.control(DeliveryItems.all),
        deliveryType: this.formBuilder.control(''),
        cartData: this.formBuilder.control(null, this.cartValidator),
        shipTo: this.formBuilder.group<ShippingAndDeliveryShipToFormGroup>({
          shipToAccount: this.formBuilder.control(null, Validators.required),
          isEdited: this.formBuilder.control(false)
        }),
        freightdeliveryoption: this.formBuilder.control('')
      }),
      paymentDetails: this.formBuilder.group<PaymentDetailsFormGroup>({
        selectedPayment: this.formBuilder.control(''),
        billTo: this.formBuilder.control(''),
        billToCC: this.formBuilder.control(null, {validators: this.billToCCValidator}),
        i2p: this.formBuilder.control('Email', {validators: this.i2pValidator}),
        i2pEmail: this.formBuilder.control('', {validators: [i2pEmailValidator]}),
        i2pSms: this.formBuilder.control('', {validators: [Validators.pattern(this.regexPhonePattern)]}),
        addedCCResult: this.formBuilder.control(''),
      }),
      additionalDetails: this.formBuilder.group<AdditionalDetailsFormGroup>({
        altEmail: this.formBuilder.control("", {validators: [isValidEmail]}),
        specialInstructions: this.formBuilder.control("")
      }),
      smsOpt: this.formBuilder.group<CheckoutSmsOptFormGroup>({
        optIn: this.formBuilder.control(false),
        contactName: this.formBuilder.control(null, {validators: Validators.required}),
        mobileNumber: this.formBuilder.control(null, {validators: Validators.compose([Validators.required, Validators.pattern(this.regexPhonePattern)])})
      }),
      procuredAllLines: this.formBuilder.control(false),
      clearShoppingBasket: this.formBuilder.control(false),
      selectedShipTo: this.formBuilder.control(null)
    });
    this.checkoutForm = newForm;
  }

  public setControl(
    name: CheckoutFormGroups,
    form: FormGroup,
    validateAfterFirstEdit: boolean): void {
    this.checkoutForm?.setControl(name, form, {emitEvent: false} );
    if (validateAfterFirstEdit) {
      form?.valueChanges.pipe(take(1)).subscribe(v => this.checkoutFormValidation.validate(this.checkoutForm));
    }
  }

  validateForm(): void {
    this.checkoutFormValidation.validate(this.checkoutForm);
  }

  getAllFormErrors(): any {
    return this.recursiveErrors(this.checkoutForm);
  }

  private recursiveErrors(control: any): any {
    if (control instanceof FormGroup) {
      let errors = {};
      const formGroup = control as FormGroup;

      Object.keys(formGroup.controls).forEach(key => {
        const subControl = formGroup.get(key);
        const subErrors = this.recursiveErrors(subControl);
        if (Object.keys(subErrors ?? []).length > 0) {
          errors = { ...errors, [key]: subErrors };
        }
      });

      return errors;
    } else {
      return control.errors;
    }
  }

  getAllValidators(): any {
    return this.recursiveValidators(this.checkoutForm);
  }

  private recursiveValidators(control: any): any {
    if (control instanceof FormGroup) {
      let validators = {};
      const formGroup = control as FormGroup;

      Object.keys(formGroup.controls).forEach(key => {
        const subControl = formGroup.get(key);
        const subValidators = this.recursiveValidators(subControl);

        if (subValidators) {
          validators = { ...validators, [key]: subValidators };
        }
      });

      return validators;
    } else {
      const controlValidators = control.validator && control.validator(control);

      return controlValidators ? { [control.name]: controlValidators } : null;
    }
  }

  cartValidator(control: AbstractControl): ValidationErrors | null {
    const cartData = control.value;
    const errors = cartData?.lineItems?.filter((x) =>
    x.hasPartsInNonPickableBins && x.deliveryOption === 'L') // TODO: Add basket returns as well
    .reduce((prev, curr) => {
      prev['customError.' + curr.partNumber] = `Front Counter Self Pick Not Allowed - Part ${curr.partNumber} is not in a pickable bin.`;
      return prev;
    }, {});

    const procurementErrors = cartData?.lineItems?.filter(x => x.partNumber !== "FREIGHT" && x.partNumber !== "DELIVERY:90").reduce((prev, lineItem) => {
      const procured = lineItem.isHotFlag || lineItem.isSTO || lineItem.isBuyout;
      const outOfStock = lineItem.quantityAvailable === 0;
      const mappedLineItem = {
        cartItem: lineItem,
        cartItemId: lineItem.cartItemId,
        deliveryOption: lineItem.deliveryOption,
        hazmat: lineItem.hazmat,
        lineId: lineItem.lineId,
        selected: false,
        hasChanges: false
      } as CartItemAndDelivery;
      const isQuote = cartData.transactionType === OrderType.Quote;
      const err = validateLineItem(mappedLineItem, isQuote, procured, lineItem.binLocation?.startsWith("SB-"), outOfStock, cartData.isUserInternal, cartData.orderNumber);
      return {...prev, ...err};
    }, {});
    const consolidatedErrors = {...procurementErrors, ...errors};
    if (Object.keys(consolidatedErrors).length > 0) {
      return consolidatedErrors;
    }
    return null;
  }

  i2pValidator(control: FormGroup<PaymentDetailsFormGroup>): ValidationErrors | null {
    const paymentDetailsFormGroup = control.parent as FormGroup<PaymentDetailsFormGroup>;
    const emailValue = paymentDetailsFormGroup?.value.i2pEmail;
    const smsValue = paymentDetailsFormGroup?.value.i2pSms;
    const emailEmpty = emailValue === null || emailValue === undefined || emailValue === '';
    const smsEmpty = smsValue === null || smsValue === undefined || smsValue === '';
    
    const i2pValue = paymentDetailsFormGroup?.value.i2p;
    const i2pEmailControl = paymentDetailsFormGroup?.controls.i2pEmail;
    const i2pSmsControl = paymentDetailsFormGroup?.controls.i2pSms;
    const billToValue = paymentDetailsFormGroup?.value.billTo;
    const billToCCValue = paymentDetailsFormGroup?.value.billToCC

    const isCCI2P = selectedPayment.CCI2P === billToCCValue;
    const isCASH_CCOnline = billToType.CASH_CCOnline === billToValue;

    const isEmailSelected = i2pType.Email.toLowerCase() === i2pValue;
    const isSmsSelected = i2pType.SMS.toLocaleLowerCase() === i2pValue;

    const validEmail = isCASH_CCOnline && isEmailSelected && !emailEmpty && i2pEmailControl.valid;
    const validSms = isCASH_CCOnline && isSmsSelected && !smsEmpty && i2pSmsControl.valid;

    if(isCCI2P && (!validEmail && !validSms)) {
      return { required: CheckoutValidationMessages['i2p.required'] };
    }
    return null;
  }

  billToCCValidator(control: AbstractControl): ValidationErrors | null{

    const rootForm = control.root as FormGroup<CheckoutFormGroup>;
    const cartData = rootForm?.value?.shippingAndDelivery?.cartData;
    const paymentDetailsFormGroup = control.parent as FormGroup<PaymentDetailsFormGroup>;

    if(paymentDetailsFormGroup?.value?.billTo !== "CASH-CCOnline" ||
      cartData?.transactionType === OrderType.Quote ) {
      return null;
    } else {
      return { required: CheckoutValidationMessages['billToCC.required'] };
    }
  }

}
